import React, { useCallback, useEffect, useState } from 'react';
import withStyles from "isomorphic-style-loader/withStyles";
import cx from "classnames";
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { api, profilePhotouploadDir } from '../../config';
import history from '../../history';
import messages from '../../locale/messages';
import { rescheduleBookingAction } from '../../actions/booking/rescheduleBookingAction';
import { shareLiveLocationAction } from '../../actions/booking/shareLiveLocationAction';

import { getAllThreadItems } from '../../lib/graphql/threads.js'
import useQueryResponse from "../../lib/hooks/useQueryResponse";
import { getBooking } from '../../actions/booking/getBooking.js';
import { readMessageAction } from '../../actions/thread/readMessageAction.js';


import StarRating from '../StarRating/StarRating';
import chatIcon from '../../../public/dashboard/messageIcon.svg';
import phoneIcon from '../../../public/dashboard/phoneIcon.svg';
import defaultIcon from '../../../public/dashboard/defaultIconMenu.svg';
import bt from '../../components/commonStyle.css';
import s from './ProviderDetails.css';


const ProviderDetails = ({ data, isNotMapPage, isAdditionalRequestPopup, noIcons, isMessage, socket }) => {

    const [isSmallDevice, setIsSmallDevice] = useState(false);
    const dispatch = useDispatch()
    // Reschedule an accepted job (parity with the app: only while status === 'approved').
    const [showReschedule, setShowReschedule] = useState(false);
    const [newTime, setNewTime] = useState('');
    const canReschedule = data?.status === 'approved';
    const doReschedule = async () => {
        if (!newTime) return;
        const unix = Math.floor(new Date(newTime).getTime() / 1000);
        if (!unix || isNaN(unix)) return;
        const ok = await dispatch(rescheduleBookingAction({ bookingId: data?.id, scheduleFrom: unix }));
        if (ok) { setShowReschedule(false); setNewTime(''); }
    };
    // SOS: share the client's live location with their emergency contacts (parity with the app).
    const doShareLocation = () => {
        const send = (loc) => dispatch(shareLiveLocationAction({ bookingId: data?.id, currentLocation: loc }));
        if (typeof navigator !== 'undefined' && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (pos) => send(`https://maps.google.com/?q=${pos.coords.latitude},${pos.coords.longitude}`),
                () => send(null),
                { timeout: 8000 }
            );
        } else {
            send(null);
        }
    };
    let phoneNumber, phoneDialCode
    phoneNumber = data?.partnerDetails?.userData?.phoneNumber, phoneDialCode = data?.partnerDetails?.userData?.phoneDialCode;

    let rating = parseFloat(data?.partnerDetails?.userData?.overallRating)
    const notShowIcons = ['scheduled', 'declined', 'cancelledByUser', 'cancelledByPartner', 'completed', 'expired'].includes(data?.status);

    const handleResizeHandler = useCallback(() => {
        setIsSmallDevice(window.matchMedia('(max-width: 767px)').matches);
    }, []);

    useEffect(() => {
        const isBrowser = typeof window !== 'undefined';
        if (isBrowser) {
            handleResizeHandler();
            window.addEventListener('resize', handleResizeHandler);
        }

        return () => {
            window.removeEventListener('resize', handleResizeHandler);
        };
    }, [handleResizeHandler]);

    const handleClick = async () => {
        await dispatch(readMessageAction({ bookingId: data?.id }))
        history.push({
            pathname: `/chat-message/${data?.id}`,
            state: { from: history.location.pathname }
        });
    }

    const { data: threadItems, loading, refetch } = useQueryResponse({
        query: getAllThreadItems,
        variables: {
            offset: 0,
            bookingId: data?.id
        },
    })

    let id = data?.id, variables = {}
    if (socket && data?.id) {
        variables = { bookingId: id, offset: 0 };
        socket.on(`bookingStatusUpdated-${id}`, () => {
            dispatch(getBooking(id))
            refetch(variables)
        })
    }

    let unReadThreadCount = data?.messageData?.unreadThreadCount

    return (
        <div className={cx(isNotMapPage ? s.bookingDetailsNameBox : isAdditionalRequestPopup ? s.additionRequestDetailsBox : s.providerCardDetailsBox)}>
            {
                data?.partnerDetails?.picture ? <div className={cx(isAdditionalRequestPopup ? s.providerCardDetailsAdditionalPopupImgContainer : s.providerCardDetailsImgContainer)}>
                    <img src={`${api.apiEndpoint}${profilePhotouploadDir}medium_${data?.partnerDetails?.picture}`} width="62" height="62" alt="userProfile" />
                </div> : <div className={cx(isAdditionalRequestPopup ? s.providerCardDetailsAdditionalPopupDefaultImgContainer : s.providerCardDetailsDefaultImgContainer)}>
                    <img src={defaultIcon} width="62" height="62" alt="defaultIcon" />
                </div>
            }
            <div className={s.providerCardDetailsInfoContainer}>
                <div className={s.providerCardDetailsHeadingBox}>
                    <h5 className={cx(bt.noMargin, bt.primaryColor, isAdditionalRequestPopup ? [bt.fontWeight600, bt.commonSize16] : [s.providerCardDetailsHeading, bt.fontWeight700])}>{data?.partnerDetails?.firstName}</h5>
                    {!noIcons && !notShowIcons && (data?.bookingType === 1 || data?.bookingType === 2) &&
                        <>
                            {isSmallDevice ? <a href={`tel:${phoneDialCode}${phoneNumber}`} className={bt.viewAllLink}>
                                <span className={bt.displayInlineBlock}>{phoneDialCode}&nbsp;</span>
                                <span className={bt.displayInlineBlock}>{phoneNumber}</span>
                            </a> :
                                <div className={s.phoneNumberLink}>
                                    <span className={bt.displayInlineBlock}>{phoneDialCode}&nbsp;</span>
                                    <span className={bt.displayInlineBlock}>{phoneNumber}</span>
                                </div>
                            }
                        </>
                    }
                    <StarRating value={rating} name={'review'} className={s.providerDetailsRatingContainer} />
                </div>
                {!noIcons && !notShowIcons && (data?.bookingType === 1 || data?.bookingType === 2) && <div className={s.providerCardDetailsIconContainer}>
                    <div className={cx(s.providerCardDetailsIcon, s.providerCardPhoneIconBox)}>
                        <a href={`tel:${phoneDialCode}${phoneNumber}`} className={bt.cursorPointer}>
                            <img src={phoneIcon} alt="phoneIcon" />
                        </a>
                    </div>
                    {!isMessage && <div className={cx(s.providerCardDetailsIcon, bt.cursorPointer)} onClick={handleClick}>
                        <img src={chatIcon} alt="chatIcon" />
                        {unReadThreadCount > 0 && <span className={cx(s.messageIconNotificationDot, "messageIconNotificationDotRtl")} />}
                    </div>}
                </div>}
                {!noIcons && canReschedule && (
                    <div className={bt.space2}>
                        {!showReschedule ? (
                            <a href={'#'} className={bt.viewAllLink} onClick={(e) => { e.preventDefault(); setShowReschedule(true); }}>
                                <FormattedMessage {...messages.rescheduleButton} />
                            </a>
                        ) : (
                            <div className={cx(bt.commonSize14)}>
                                <div className={bt.space2}><FormattedMessage {...messages.rescheduleTitle} /></div>
                                <input
                                    type="datetime-local"
                                    value={newTime}
                                    onChange={(e) => setNewTime(e.target.value)}
                                    className={cx(bt.commonSize14, bt.space2)}
                                />
                                <a href={'#'} className={cx(bt.viewAllLink, bt.space2)} onClick={(e) => { e.preventDefault(); doReschedule(); }}>
                                    <FormattedMessage {...messages.rescheduleButton} />
                                </a>
                            </div>
                        )}
                    </div>
                )}
                {!noIcons && !notShowIcons && (data?.bookingType === 1 || data?.bookingType === 2) && (
                    <div className={bt.space2}>
                        <a href={'#'} className={bt.viewAllLink} onClick={(e) => { e.preventDefault(); doShareLocation(); }}>
                            <FormattedMessage {...messages.shareLocationButton} />
                        </a>
                    </div>
                )}
            </div>
        </div>
    )
}

export default withStyles(bt, s)(ProviderDetails);
